import { AlertTriangle, Check, X } from "lucide-react";
import type { Readiness, UtenzaFornitoreDTO } from "@/types";

interface Props {
  readiness: Readiness | null | undefined;
  // Interruttori auto-invio (globale + agenzia): a false una pratica completa
  // non parte da sola.
  autoAttivo?: boolean;
  // Utenza fornitore con cui parte questa pratica: fuori uso, spiega perché una
  // pratica completa resta ferma.
  utenza?: UtenzaFornitoreDTO | null;
  // La lettura dell'utenza è fallita: `utenza` è null ma non perché manchi
  // l'assegnazione — non si accusa l'amministratore di un guasto nostro.
  utenzaNonDisponibile?: boolean;
}

const MOTIVO_UTENZA: Record<string, string> = {
  da_ripristinare:
    "Utenza A2A da ripristinare: le pratiche restano in coda finché l'Affiliato non la ripristina.",
  sessione_scaduta: "Sessione A2A scaduta: riaccedi per inviare e aggiornare.",
  da_configurare: "Credenziali A2A da inserire.",
};

// Nessuna utenza assegnata all'agenzia: il backend manda `utenza_fornitore` a null
// e la pratica, per quanto completa, non parte (nessun ripiego sulla predefinita).
const UTENZA_NON_ASSEGNATA =
  "Utenza A2A non assegnata: le pratiche restano in coda finché l'amministratore non la assegna.";

// Pannello dettaglio pratica: checklist "cosa manca" per l'invio ad A2A.
// Nasconde il pannello solo se il backend non ha calcolato la readiness (enrichment
// fallito/omesso); se applicabile=false spiega comunque perché non c'è checklist.
export function ReadinessPanel({ readiness, autoAttivo = false, utenza, utenzaNonDisponibile = false }: Props) {
  if (!readiness) return null;
  // Pratica già partita: non c'è nulla da preparare e «va inviata manualmente» sarebbe
  // falso (segnalazione 2026-09-07 su una pratica accettata). A che punto sia lo dice
  // la barra degli stati in cima alla scheda.
  if (readiness.motivo === "gia_inviata") return null;
  const motivoUtenza = utenza === null
    ? (utenzaNonDisponibile ? null : UTENZA_NON_ASSEGNATA)
    : utenza && utenza.stato !== "attiva" ? MOTIVO_UTENZA[utenza.stato] : null;
  return (
    <div className="bg-card p-4 rounded-lg border border-border">
      <h3 className="font-semibold text-text mb-3">Cosa manca per l'invio</h3>
      {readiness.motivo === "strada_vecchia" ? (
        // ⚠ Distinta da «va inviata manualmente» qui sotto, che vuol dire «premi il
        // bottone tu»: questa pratica dal portale non parte affatto, e il bottone è
        // spento. Mandare l'operatore a cercarlo sarebbe una bugia.
        <p className="flex items-start gap-2 text-sm text-amber-700">
          <AlertTriangle size={16} className="shrink-0 mt-0.5" />
          Questa pratica non si invia dal portale: va inserita a mano sul portale A2A.
        </p>
      ) : !readiness.applicabile ? (
        <p className="text-sm text-muted-foreground">
          Questa pratica non rientra nell'invio automatico ad A2A: va inviata manualmente.
        </p>
      ) : readiness.pronta ? (
        motivoUtenza ? (
          <p className="flex items-start gap-2 text-sm text-amber-700">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
            {motivoUtenza}
          </p>
        ) : utenza?.modalita === "sessione_temporanea" ? (
          <p className="flex items-center gap-2 text-sm text-green-700">
            <Check size={16} className="shrink-0" />
            Pratica completa: si invia da qui con «Invia ad A2A».
          </p>
        ) : autoAttivo ? (
          <p className="flex items-center gap-2 text-sm text-green-700">
            <Check size={16} className="shrink-0" />
            Pronta per l'invio automatico ad A2A.
          </p>
        ) : (
          <p className="flex items-center gap-2 text-sm text-amber-700">
            <Check size={16} className="shrink-0" />
            Pratica completa: l'invio automatico è spento, va inviata a mano.
          </p>
        )
      ) : (
        <ul className="space-y-1.5 text-sm">
          {readiness.manca.map((m) => (
            <li key={m} className="flex items-center gap-2 text-amber-700">
              <X size={14} className="shrink-0" />
              {m}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
